import { computed, ref, watch, type Ref } from "vue";
import { refDebounced } from "@vueuse/core";
import { useQuery } from "@tanstack/vue-query";
import { useApi } from "@shopexperts/shared";
import { useRouter } from "vue-router";
import type { DirectChatSearchResult } from "@shopexperts/legacy-api";
import { getConvexAuthInfo } from "../convexClient";

export function useDirectChatSearch(side: Ref<"client" | "expert">) {
  const api = useApi();
  const router = useRouter();
  const search = ref("");
  const term = computed(() => search.value.trim());
  const debounced = refDebounced(term, 300);
  const cursor = ref<string | null>(null);
  watch(term, () => { cursor.value = null; });
  const results = useQuery({
    queryKey: computed(() => ["direct-chat-search", getConvexAuthInfo()?.orgId, side.value, debounced.value, cursor.value]),
    enabled: computed(() => term.value.length >= 2 && term.value === debounced.value),
    queryFn: () => api.directChatrooms.search({ query: { q: debounced.value, ...(cursor.value ? { cursor: cursor.value } : {}) } }),
    staleTime: 0,
    retry: false,
  });
  const waiting = computed(() => term.value !== debounced.value || results.isFetching.value);
  const opening = ref<number | null>(null);
  const openError = ref<string | null>(null);
  watch(term, () => { openError.value = null; });
  async function open(result: DirectChatSearchResult) {
    if (opening.value !== null) return;
    openError.value = null;
    if (side.value === "expert" && !result.thread_id) {
      openError.value = "Only existing conversations can be opened.";
      return;
    }
    opening.value = result.org_id;
    try {
      const threadId = result.thread_id ?? (await api.directChatrooms.open({ body: { recipient_org_id: result.org_id } })).thread_id;
      await router.push(`/${side.value}/messages/${threadId}`);
    } catch {
      openError.value = "Could not open the conversation. Please try again.";
    } finally {
      opening.value = null;
    }
  }
  return { search, term, results, waiting, cursor, opening, openError, open };
}
